import type { components, paths } from "@sagot/api-client";
import type { StackedDatum, StatDelta } from "@sagot/ui";

export type AnalyticsQueryParams = NonNullable<paths["/v1/analytics/summary"]["get"]["parameters"]["query"]>;
export type ChannelType = components["schemas"]["ChannelType"];
export type TimeseriesPoint = components["schemas"]["TimeseriesPoint"];
export type RangePreset = "7d" | "30d" | "90d" | "custom";

export const RANGE_PRESETS: readonly RangePreset[] = ["7d", "30d", "90d", "custom"];
/** Console-visible channels (playground traffic is excluded from analytics server-side). */
export const ANALYTICS_CHANNELS: readonly ChannelType[] = ["widget", "webapp", "mobile", "messenger", "email", "api", "slack", "teams"];
export const MAX_CUSTOM_DAYS = 366;
export const DEFAULT_RANGE: RangePreset = "30d";
export const HEATMAP_DAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const;
export const HOURS_PER_DAY = 24;
const DAY_MS = 86_400_000;
const AXIS_TICKS = 5;
const MS_PER_SECOND = 1000;
const SECONDS_PER_MINUTE = 60;

export interface AnalyticsFilters {
  range: RangePreset;
  from: string;
  to: string;
  agentId: string | null;
  channel: ChannelType | null;
}

const isPreset = (value: string | null): value is RangePreset => RANGE_PRESETS.some((r) => r === value);
const isChannel = (value: string | null): value is ChannelType => ANALYTICS_CHANNELS.some((c) => c === value);

export function readFilters(params: URLSearchParams): AnalyticsFilters {
  const range = params.get("range");
  const channel = params.get("channel");
  return {
    range: isPreset(range) ? range : DEFAULT_RANGE,
    from: params.get("from") ?? "",
    to: params.get("to") ?? "",
    agentId: params.get("agent") ?? null,
    channel: isChannel(channel) ? channel : null,
  };
}

export function writeFilters(filters: AnalyticsFilters): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.range !== DEFAULT_RANGE) params.set("range", filters.range);
  if (filters.range === "custom") {
    if (filters.from) params.set("from", filters.from);
    if (filters.to) params.set("to", filters.to);
  }
  if (filters.agentId) params.set("agent", filters.agentId);
  if (filters.channel) params.set("channel", filters.channel);
  return params;
}

/** A custom range is usable once both dates are set, ordered and ≤ 366 days apart. */
export function isRangeValid(filters: AnalyticsFilters): boolean {
  if (filters.range !== "custom") return true;
  if (!filters.from || !filters.to) return false;
  const span = (Date.parse(filters.to) - Date.parse(filters.from)) / DAY_MS;
  return Number.isFinite(span) && span >= 0 && span < MAX_CUSTOM_DAYS;
}

export function toQueryParams(filters: AnalyticsFilters): AnalyticsQueryParams {
  const query: AnalyticsQueryParams = { range: filters.range };
  if (filters.range === "custom") Object.assign(query, { from: filters.from, to: filters.to });
  if (filters.agentId) query.agent_id = filters.agentId;
  if (filters.channel) query.channel = filters.channel;
  return query;
}

type DeltaLabel = (value: string) => string;

/** Relative change (%) → StatCard delta; `upIsGood` drives the tone (icon + text always shown). */
export function percentDelta(value: number | null | undefined, label: DeltaLabel, upIsGood = true): StatDelta | undefined {
  if (value === null || value === undefined) return undefined;
  const sign = value > 0 ? "+" : value < 0 ? "−" : "";
  return { direction: value >= 0 ? "up" : "down", label: label(`${sign}${Math.abs(Math.round(value)).toString()}%`), positive: value >= 0 === upIsGood };
}

/** Rate change in percentage points → StatCard delta. */
export function pointsDelta(value: number | null | undefined, label: DeltaLabel, upIsGood = true): StatDelta | undefined {
  if (value === null || value === undefined) return undefined;
  const sign = value > 0 ? "+" : value < 0 ? "−" : "";
  return { direction: value >= 0 ? "up" : "down", label: label(`${sign}${Math.abs(value).toFixed(1)}`), positive: value >= 0 === upIsGood };
}

/** "1.4s" under a minute, "6m 40s" above. */
export function durationLabel(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) return "—";
  const seconds = ms / MS_PER_SECOND;
  if (seconds < SECONDS_PER_MINUTE) return `${seconds.toFixed(1)}s`;
  const minutes = Math.floor(seconds / SECONDS_PER_MINUTE);
  return `${minutes.toString()}m ${Math.round(seconds % SECONDS_PER_MINUTE).toString()}s`;
}

/** Always 7 × 24 (Mon…Sun × 0…23), padding missing cells with zero. */
export function heatmapValues(cells: readonly (readonly number[])[]): number[][] {
  return HEATMAP_DAYS.map((_, d) => Array.from({ length: HOURS_PER_DAY }, (_, h) => cells[d]?.[h] ?? 0));
}

/** Up to five evenly spaced labels (first and last included). */
export function axisTicks(labels: readonly string[], count = AXIS_TICKS): string[] {
  if (labels.length <= count) return [...labels];
  const step = (labels.length - 1) / (count - 1);
  return Array.from({ length: count }, (_, i) => labels[Math.round(i * step)] ?? "");
}

/** Previous-period values aligned by position to the current series (padded with zero). */
export function alignedPrevious(current: readonly TimeseriesPoint[], previous: readonly TimeseriesPoint[]): number[] {
  return current.map((_, i) => previous[i]?.conversations ?? 0);
}

export function weeklyStacks(points: readonly TimeseriesPoint[], label: (date: string) => string): StackedDatum[] {
  return points.map((p) => ({ label: label(p.date), values: { ai_resolved: p.ai_resolved, handed_off: p.handed_off, abandoned: p.abandoned } }));
}

export function hasActivity(points: readonly TimeseriesPoint[]): boolean {
  return points.some((p) => p.conversations > 0 || p.ai_resolved > 0 || p.handed_off > 0 || p.abandoned > 0);
}

/** Share bar widths in whole percent that always sum to 100 (largest remainder). */
export function shareWidths(pcts: readonly number[]): number[] {
  const raw = pcts.map((p) => Math.max(p, 0) * 100);
  const floors = raw.map(Math.floor);
  let left = Math.round(raw.reduce((a, b) => a + b, 0)) - floors.reduce((a, b) => a + b, 0);
  const order = raw.map((r, i) => ({ i, rem: r - Math.floor(r) })).sort((a, b) => b.rem - a.rem);
  for (const { i } of order) {
    if (left <= 0) break;
    floors[i] = (floors[i] ?? 0) + 1;
    left -= 1;
  }
  return floors;
}

/** Date-only ISO (YYYY-MM-DD) → a Date at UTC noon, so formatting in any TZ keeps the day. */
export function calendarDate(isoDate: string): string {
  return `${isoDate.slice(0, 10)}T12:00:00Z`;
}
